import React, { useState, useEffect } from 'react';
import {
  Upload,
  Image as ImageIcon,
  Video,
  Film,
  Trash2,
  MapPin,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  X,
  ArrowRight,
  Shield,
  Loader2,
  ChevronDown,
  ChevronUp,
  Wrench,
  Info,
  Clock,
  Coins,
  Check,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { SERVICE_CATEGORIES } from '../../data/categories';
import { LocationMapInput, SelectedLocationData } from '../../components/location/LocationMapInput';
import { CategoryIcon } from '../../components/common/CategoryIcon';
import {
  UrgencyLevel,
  AIEstimateResult,
  AIJobAnalysis,
  AIClassificationResult,
  AISafetyHazard,
  AIIntentValidation,
} from '../../types';
import { tradeToSlug } from '../../lib/matchingEngine';

// Quick example templates to test natural phrasing without requiring category preselection
const NATURAL_PROBLEM_PROMPTS = [
  {
    label: 'Leaking Sink',
    text: 'My kitchen sink has been leaking from underneath for two days.',
  },
  {
    label: 'Generator Fault',
    text: 'My generator starts but goes off after a few minutes.',
  },
  {
    label: 'AC Not Cooling',
    text: 'My AC is blowing air but the room isn\'t getting cold.',
  },
  {
    label: 'Electrical Tripping',
    text: 'The light in my house keeps going off whenever I turn on the pressing iron.',
  },
  {
    label: 'Borehole Issue',
    text: 'My borehole pump is making noise but no water is coming out.',
  },
  {
    label: 'CCTV Installation',
    text: 'I need someone to install a CCTV system.',
  },
  {
    label: 'Solar Inverter',
    text: 'I need a solar inverter technician.',
  },
];

function slugMatchesTrade(slug: string, trade: string): boolean {
  const s = slug.toLowerCase();
  const t = trade.toLowerCase();
  const detectedSlug = tradeToSlug(t);
  if (s === detectedSlug) return true;
  if (s === 'plumber' && t.includes('plumb')) return true;
  if (s === 'electrician' && t.includes('electr')) return true;
  if (s === 'ac-technician' && (t.includes('ac') || t.includes('cool') || t.includes('air cond'))) return true;
  if (s === 'generator-technician' && (t.includes('gen') || t.includes('mikano') || t.includes('firman'))) return true;
  if (s === 'carpenter' && t.includes('carpent')) return true;
  if (s === 'furniture-maker' && (t.includes('furnit') || t.includes('cabinet') || t.includes('wood'))) return true;
  if (s === 'painter' && t.includes('paint')) return true;
  if (s === 'tiler' && t.includes('tile')) return true;
  if (s === 'welder' && t.includes('weld')) return true;
  if (s === 'appliance-repair' && (t.includes('appliance') || t.includes('fridge') || t.includes('washing'))) return true;
  if (s === 'bricklayer' && (t.includes('brick') || t.includes('mason') || t.includes('block'))) return true;
  return s === t;
}

function tradeToCategorySlug(trade: string): string {
  return tradeToSlug(trade);
}

// Client-side image compression for fast network transport and instant AI inference
async function compressImageForAI(file: File, maxDim = 1024, quality = 0.75): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve((e.target?.result as string) || '');
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => resolve((e.target?.result as string) || '');
      img.src = (e.target?.result as string) || '';
    };
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
}

export const PostJobPage: React.FC = () => {
  const { currentUser, postJob, navigateTo, viewParams } = useApp();

  const savedDraft = React.useMemo(() => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const raw = window.sessionStorage.getItem('jobmatch_post_job_draft');
        if (raw) return JSON.parse(raw);
      }
    } catch (_e) {}
    return null;
  }, []);

  // Natural Problem Input state
  const [description, setDescription] = useState<string>(savedDraft?.description || '');
  const [title, setTitle] = useState<string>(savedDraft?.title || '');
  
  // Category state (starts flexible; AI suggestions auto-populate it)
  const [categorySlug, setCategorySlug] = useState<string>(
    viewParams.selectedCategory || viewParams.category || savedDraft?.categorySlug || 'plumber'
  );
  const [userManuallySelectedCategory, setUserManuallySelectedCategory] = useState<boolean>(
    Boolean(viewParams.selectedCategory || viewParams.category || savedDraft?.userManuallySelectedCategory)
  );
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  // Direct Professional Selection (when customer clicks "Post Job / Request Quote" on a specific artisan)
  const [targetProfessionalId, setTargetProfessionalId] = useState<string | undefined>(
    viewParams.targetProfessionalId || viewParams.selectedProfessionalId || savedDraft?.targetProfessionalId
  );
  const [targetProfessionalName, setTargetProfessionalName] = useState<string | undefined>(
    viewParams.targetProfessionalName || viewParams.selectedProfessionalName || savedDraft?.targetProfessionalName
  );
  const [targetProfessionalBusiness, setTargetProfessionalBusiness] = useState<string | undefined>(
    viewParams.targetProfessionalBusiness || savedDraft?.targetProfessionalBusiness
  );
  const [targetProfessionalAvatar, setTargetProfessionalAvatar] = useState<string | undefined>(
    viewParams.targetProfessionalAvatar || savedDraft?.targetProfessionalAvatar
  );

  // Nigerian Location & Admin state
  const [state, setState] = useState<string>(savedDraft?.state || currentUser?.state || 'Lagos');
  const [lga, setLga] = useState<string>(savedDraft?.lga || currentUser?.lga || 'Ikeja');
  const [cityArea, setCityArea] = useState<string>(savedDraft?.cityArea || currentUser?.cityArea || '');
  const [address, setAddress] = useState<string>(savedDraft?.address || currentUser?.address || '');
  const [formattedAddress, setFormattedAddress] = useState<string>(savedDraft?.formattedAddress || currentUser?.address || '');
  const [locationLandmark, setLocationLandmark] = useState<string>(savedDraft?.locationLandmark || '');
  const [locationLatitude, setLocationLatitude] = useState<number | undefined>(savedDraft?.locationLatitude);
  const [locationLongitude, setLocationLongitude] = useState<number | undefined>(savedDraft?.locationLongitude);
  const [placeId, setPlaceId] = useState<string | undefined>(savedDraft?.placeId);
  const [locationConfirmed, setLocationConfirmed] = useState<boolean>(true);
  const [locationConfidence, setLocationConfidence] = useState<'geoapify_confirmed' | 'verified_location' | 'manual_confirmed'>('manual_confirmed');
  const [lgaConfirmed, setLgaConfirmed] = useState<boolean>(true);

  // Ref for aborting in-flight AI requests to prevent lagging responses
  const abortControllerRef = React.useRef<AbortController | null>(null);
  // Signature of last analyzed input to avoid repeating AI calls when description/media haven't changed
  const lastAnalyzedSignatureRef = React.useRef<string>('');
  // Guard to ensure job form only submits when user explicitly clicks the Post Job submit button (never on mobile camera return)
  const explicitSubmitAllowedRef = React.useRef<boolean>(false);

  // Availability & Location Type
  const [locationType, setLocationType] = useState<'Home' | 'Office' | 'Shop/Business' | 'Other'>('Home');
  const [urgency, setUrgency] = useState<UrgencyLevel>(savedDraft?.urgency || 'within_48h');
  const [budgetEstimate, setBudgetEstimate] = useState<number | ''>(savedDraft?.budgetEstimate ?? '');
  const [preferredDate, setPreferredDate] = useState<string>('');
  const [preferredTime, setPreferredTime] = useState<string>('');
  const [timePreference, setTimePreference] = useState<'exact' | 'flexible'>('flexible');
  const [flexibleTimeWindow, setFlexibleTimeWindow] = useState<string>('2:00 PM – 4:00 PM');

  // Media (Photos & Video Upload)
  const [photos, setPhotos] = useState<string[]>(Array.isArray(savedDraft?.photos) ? savedDraft.photos : []);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [videoPreviews, setVideoPreviews] = useState<
    Array<{ file: File; url: string; name: string; sizeMb: string }>
  >([]);
  const [videoError, setVideoError] = useState<string>('');

  // Gemini AI Job Understanding State
  const [isEstimating, setIsEstimating] = useState(false);
  const [aiEstimate, setAiEstimate] = useState<AIEstimateResult | null>(savedDraft?.aiEstimate || null);
  const [aiClassification, setAiClassification] = useState<AIClassificationResult | null>(savedDraft?.aiClassification || null);
  const [intentValidation, setIntentValidation] = useState<AIIntentValidation | null>(savedDraft?.intentValidation || null);
  const [safetyHazard, setSafetyHazard] = useState<AISafetyHazard | string | null>(savedDraft?.safetyHazard || null);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<AIJobAnalysis | null>(savedDraft?.aiAnalysisResult || null);
  const [switchedSuccessNotice, setSwitchedSuccessNotice] = useState<string>('');
  const [aiError, setAiError] = useState('');

  // Follow-up diagnostic questions answered by customer
  const [conversationAnswers, setConversationAnswers] = useState<Record<string, string>>(savedDraft?.conversationAnswers || {});

  // Submitting state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzingAI, setIsAnalyzingAI] = useState(false);
  const [showCategoryMismatchModal, setShowCategoryMismatchModal] = useState(false);
  const [pendingAnalysis, setPendingAnalysis] = useState<AIJobAnalysis | null>(null);
  const [pendingClassification, setPendingClassification] = useState<AIClassificationResult | null>(null);

  const selectedCategoryObj = SERVICE_CATEGORIES.find((c) => c.slug === categorySlug);

  // Keep PostJobPage active flag and draft synced in sessionStorage so taking a camera photo never loses state or redirects
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem('jobmatch_post_job_active', 'true');
      }
    } catch (_e) {}
  }, []);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(
          'jobmatch_post_job_draft',
          JSON.stringify({
            description,
            title,
            categorySlug,
            userManuallySelectedCategory,
            targetProfessionalId,
            targetProfessionalName,
            targetProfessionalBusiness,
            targetProfessionalAvatar,
            state,
            lga,
            cityArea,
            address,
            formattedAddress,
            locationLandmark,
            locationLatitude,
            locationLongitude,
            placeId,
            urgency,
            budgetEstimate,
            photos,
            aiEstimate,
            aiClassification,
            intentValidation,
            safetyHazard,
            aiAnalysisResult,
            conversationAnswers,
          })
        );
      }
    } catch (_e) {}
  }, [
    description,
    title,
    categorySlug,
    userManuallySelectedCategory,
    targetProfessionalId,
    targetProfessionalName,
    targetProfessionalBusiness,
    targetProfessionalAvatar,
    state,
    lga,
    cityArea,
    address,
    formattedAddress,
    locationLandmark,
    locationLatitude,
    locationLongitude,
    placeId,
    urgency,
    budgetEstimate,
    photos,
    aiEstimate,
    aiClassification,
    intentValidation,
    safetyHazard,
    aiAnalysisResult,
    conversationAnswers,
  ]);

  // Build input signature for deduplicating unchanged descriptions/media
  const computeInputSignature = (descText: string, photoList: string[], videoList: any[]) => {
    return `${descText.trim().toLowerCase()}|${photoList.length}|${videoList.length}`;
  };

  // Reset notice when user edits problem text
  useEffect(() => {
    setSwitchedSuccessNotice('');
  }, [description]);

  // Quick Problem Example Click
  const handleSelectExample = (text: string) => {
    setDescription(text);
    if (!title) {
      setTitle(text.slice(0, 60));
    }
    setAiError('');
    // Trigger AI analysis with this example
    runAiJobUnderstanding(text, title || text.slice(0, 60));
  };

  // Photo Upload Handler with Client-Side Compression (Safe against camera reload / form submit)
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    setMediaFiles((prev) => [...prev, ...files]);

    const newCompressedUrls: string[] = [];
    for (const file of files) {
      try {
        const compressedDataUrl = await compressImageForAI(file);
        if (compressedDataUrl) {
          newCompressedUrls.push(compressedDataUrl);
        }
      } catch (err) {
        console.warn('Photo compression fallback notice:', err);
      }
    }

    if (newCompressedUrls.length > 0) {
      setPhotos((prev) => [...prev, ...newCompressedUrls]);
    }

    // Reset file input value so the customer can take or upload additional photos smoothly
    e.target.value = '';
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Video File Upload Handler
  const handleVideoFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setVideoError('');

    const maxFileSize = 50 * 1024 * 1024; // 50MB
    const allowedExtensions = ['mp4', 'mov', 'webm', '3gp', 'mkv'];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const isVideo = file.type.startsWith('video/') || allowedExtensions.includes(ext);

      if (!isVideo) {
        setVideoError(`"${file.name}" is not a recognized video format. Please upload MP4, MOV, WebM, or 3GP.`);
        return;
      }

      if (file.size > maxFileSize) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
        setVideoError(`"${file.name}" is ${sizeMb} MB, which exceeds the 50MB limit.`);
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);

      setVideoFiles((prev) => [...prev, file]);
      setVideoPreviews((prev) => [...prev, { file, url: previewUrl, name: file.name, sizeMb }]);
    }
  };

  const handleRemoveVideo = (index: number) => {
    const target = videoPreviews[index];
    if (target && target.url) {
      try {
        URL.revokeObjectURL(target.url);
      } catch (e) {}
    }
    setVideoPreviews((prev) => prev.filter((_, i) => i !== index));
    setVideoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Interactive follow-up question answer update
  const handleAnswerQuestion = (question: string, answer: string) => {
    setConversationAnswers((prev) => ({
      ...prev,
      [question]: answer,
    }));
  };

  // CORE AI UNDERSTANDING FUNCTION (Single Unified Request, Deduplicated, Abort-Controlled)
  const runAiJobUnderstanding = async (customDesc?: string, customTitle?: string, forceRefresh = false) => {
    const activeDesc = (customDesc ?? description).trim();
    const activeTitle = (customTitle ?? title).trim();
    const hasAttachedMedia = photos.length > 0 || videoPreviews.length > 0;

    if (!activeDesc && !activeTitle && !hasAttachedMedia) {
      setAiError('Please describe the problem or attach a photo/video of the fault first.');
      return;
    }

    const inputSig = computeInputSignature(activeDesc, photos, videoPreviews);
    // Avoid repeating analysis when customer has not changed description or media
    if (!forceRefresh && lastAnalyzedSignatureRef.current === inputSig && (aiAnalysisResult || intentValidation)) {
      return;
    }

    // Abort previous in-flight request to prevent lagging race conditions
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setAiError('');
    setIsEstimating(true);

    try {
      const res = await fetch('/api/ai/analyze-and-estimate-job', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          category: userManuallySelectedCategory ? (selectedCategoryObj?.name || categorySlug) : '',
          title: activeTitle || activeDesc.slice(0, 70),
          description: activeDesc,
          state,
          lga,
          images: photos,
          videos: videoPreviews.map((v) => v.name),
          conversationAnswers,
        }),
      });

      const data = await res.json();
      if (data.success) {
        lastAnalyzedSignatureRef.current = inputSig;

        // Intent Check
        if (data.intentValidation) {
          setIntentValidation(data.intentValidation);
          if (data.intentValidation.intent !== 'VALID_JOB') {
            setAiEstimate(null);
            setAiClassification(data.classification || null);
            setSafetyHazard(null);
            return;
          }
        } else {
          setIntentValidation(null);
        }

        // Auto-populate Title if user hasn't typed one
        if (!title.trim()) {
          if (data.normalizedTitle) {
            setTitle(data.normalizedTitle);
          } else if (data.analysis?.problem_summary) {
            setTitle(data.analysis.problem_summary.slice(0, 70));
          }
        }

        // Set AI Analysis Result
        if (data.analysis) {
          setAiAnalysisResult(data.analysis);

          // Auto-select trade category if customer didn't manually pick one
          const detectedTrade = data.analysis.recommended_service || data.analysis.detected_category;
          if (detectedTrade && !userManuallySelectedCategory) {
            const mappedSlug = tradeToCategorySlug(detectedTrade);
            if (mappedSlug && mappedSlug !== 'other') {
              setCategorySlug(mappedSlug);
            }
          }

          if (data.analysis.urgency && data.analysis.urgency !== urgency) {
            setUrgency(data.analysis.urgency);
          }
        }

        // Set Estimate
        if (data.estimate) {
          setAiEstimate(data.estimate);
          if (data.estimate.estimatedBudgetNaira?.recommended && !budgetEstimate) {
            setBudgetEstimate(data.estimate.estimatedBudgetNaira.recommended);
          }
        }

        if (data.classification) {
          setAiClassification(data.classification);
        }

        if (data.safetyWarning) {
          setSafetyHazard(data.safetyWarning);
        } else {
          setSafetyHazard(null);
        }
      } else {
        setAiError(data.error || 'Could not analyze problem. You can proceed with standard submission.');
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return; // Silently exit on superseded request
      }
      console.warn('AI analysis call exception:', err);
      setAiError('Instant AI analysis temporarily unavailable. You can proceed to submit your job.');
    } finally {
      setIsEstimating(false);
    }
  };

  // Switch category directly from suggestion banner
  const handleSwitchCategoryDirectly = (targetTrade: string) => {
    const newSlug = tradeToCategorySlug(targetTrade);
    setCategorySlug(newSlug);
    setUserManuallySelectedCategory(true);
    setSwitchedSuccessNotice(`Switched category to ${targetTrade}! Verified ${targetTrade}s will be prioritized.`);
    if (aiClassification) {
      setAiClassification({
        ...aiClassification,
        categoryMismatch: false,
        selectedService: targetTrade,
      });
    }
  };

  // Core execution of job creation
  const executePost = async (analysis: AIJobAnalysis | null, targetSlug: string) => {
    if (!currentUser) {
      navigateTo('customer_auth', { authMode: 'login', returnView: 'post_job' });
      return;
    }

    setIsSubmitting(true);
    const targetCategoryObj =
      SERVICE_CATEGORIES.find((c) => c.slug === targetSlug) || selectedCategoryObj;

    const finalTitle = title.trim() || analysis?.problem_summary?.slice(0, 70) || description.slice(0, 60) || 'Artisan Service Request';

    const answeredEntries = Object.entries(conversationAnswers).filter(
      ([, ans]) => typeof ans === 'string' && ans.trim().length > 0
    );
    const answeredRequirements = answeredEntries.map(
      ([q, a]) => `Customer clarification — ${q}: ${a.trim()}`
    );

    try {
      await postJob(
        {
          customerId: currentUser.id,
          customerName: currentUser.fullName,
          customerPhone: currentUser.phone,
          customerAvatar: currentUser.avatarUrl,
          categorySlug: targetSlug,
          categoryName: targetCategoryObj?.name || 'General Repair',
          normalizedOccupation: analysis?.normalized_occupation,
          parentFamily: analysis?.parent_family,
          requiredSkills: analysis?.required_skills,
          title: finalTitle,
          description,
          state,
          lga,
          cityArea: cityArea || undefined,
          address,
          formattedAddress: formattedAddress || address,
          locationLandmark: locationLandmark.trim() || undefined,
          locationLatitude,
          locationLongitude,
          placeId,
          locationConfirmed,
          locationConfidence,
          lgaConfirmed,
          urgency: (analysis?.urgency as UrgencyLevel) || urgency,
          budgetEstimate: Number(budgetEstimate) || 25000,
          photos,
          videos: videoPreviews.map((v) => v.url),
          selectedProfessionalId: targetProfessionalId || undefined,
          selectedProfessionalName: targetProfessionalName || undefined,
          aiAnalysis: analysis
            ? {
                ...analysis,
                conversation_answers: conversationAnswers,
                known_job_requirements: Array.from(
                  new Set([...(analysis.known_job_requirements || []), ...answeredRequirements])
                ),
              }
            : undefined,
          aiScopeEstimate: aiEstimate
            ? {
                ...aiEstimate,
                knownJobRequirements: Array.from(
                  new Set([...(aiEstimate.knownJobRequirements || []), ...answeredRequirements])
                ),
              }
            : undefined,
        },
        mediaFiles,
        videoFiles
      );
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem('jobmatch_post_job_draft');
          window.sessionStorage.removeItem('jobmatch_post_job_active');
        }
      } catch (_e) {}
    } finally {
      setIsSubmitting(false);
      setIsAnalyzingAI(false);
    }
  };

  const handleAcceptDetectedCategory = () => {
    if (!pendingAnalysis) return;
    const newSlug = tradeToCategorySlug(pendingAnalysis.detected_category);
    setCategorySlug(newSlug);
    setUserManuallySelectedCategory(true);
    setShowCategoryMismatchModal(false);
    executePost(pendingAnalysis, newSlug);
  };

  const handleKeepOriginalCategory = () => {
    if (!pendingAnalysis) return;
    setShowCategoryMismatchModal(false);
    executePost(pendingAnalysis, categorySlug);
  };

  // Job Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent implicit mobile camera/keyboard form submission from navigating away to dashboard
    if (!explicitSubmitAllowedRef.current) {
      return;
    }
    explicitSubmitAllowedRef.current = false;

    if (!description.trim()) {
      alert('Please describe what you need help with.');
      return;
    }

    if (!address.trim()) {
      alert('Please provide your location address so we can match local professionals in your LGA.');
      return;
    }

    const currentSig = computeInputSignature(description, photos, videoPreviews);

    // If AI analysis was already run and description/media have not changed, reuse directly (0 extra API calls)
    if (aiAnalysisResult && lastAnalyzedSignatureRef.current === currentSig) {
      await executePost(aiAnalysisResult, categorySlug);
      return;
    }

    // Otherwise, perform AI understanding pass first
    setIsAnalyzingAI(true);
    setAiError('');

    try {
      const res = await fetch('/api/ai/analyze-and-estimate-job', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title || description.slice(0, 60),
          description,
          category: userManuallySelectedCategory ? (selectedCategoryObj?.name || categorySlug) : '',
          state,
          lga,
          images: photos,
          videos: videoPreviews.map((v) => v.url),
          conversationAnswers,
        }),
      });

      const data = await res.json();
      if (data.success && data.analysis) {
        lastAnalyzedSignatureRef.current = currentSig;
        const analysis: AIJobAnalysis = data.analysis;
        setAiAnalysisResult(analysis);
        if (data.estimate && !aiEstimate) {
          setAiEstimate(data.estimate);
        }
        if (data.safetyWarning) {
          setSafetyHazard(data.safetyWarning);
        }

        const classification: AIClassificationResult | undefined = data.classification;
        const matchesSelected = classification
          ? !classification.categoryMismatch
          : slugMatchesTrade(categorySlug, analysis.detected_category);

        // If user manually chose a category and it mismatches with AI, show discrepancy modal
        if (userManuallySelectedCategory && !matchesSelected) {
          setPendingAnalysis(analysis);
          setPendingClassification(classification || null);
          setShowCategoryMismatchModal(true);
          setIsAnalyzingAI(false);
          return;
        }

        // Auto-select AI recommended trade if customer had not manually chosen
        const detectedSlug = tradeToCategorySlug(analysis.detected_category);
        const targetSlug = (!userManuallySelectedCategory && detectedSlug && detectedSlug !== 'other')
          ? detectedSlug
          : categorySlug;

        await executePost(analysis, targetSlug);
      } else {
        await executePost(null, categorySlug);
      }
    } catch (err) {
      console.warn('AI analysis call fallback:', err);
      await executePost(null, categorySlug);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-3.5 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="post-job-light-surface bg-white text-slate-900 rounded-2xl sm:rounded-3xl p-4 sm:p-10 border border-slate-200 shadow-xl">
        
        {/* HEADER SECTION: Problem-First Marketplace Framing */}
        <div className="mb-6 sm:mb-8 text-center sm:text-left">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-bold mb-2">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span>JobMatch AI • Problem-First Artisan Marketplace</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            What do you need help with?
          </h1>
          <p className="text-xs sm:text-sm text-slate-700 mt-1 max-w-2xl leading-relaxed font-medium">
            Describe the problem in your own words. You don't need to know what type of professional you need — our AI understands your request and matches certified local specialists.
          </p>
        </div>

        {/* DIRECT PROFESSIONAL REQUEST BANNER */}
        {targetProfessionalId && (
          <div className="mb-6 p-4 rounded-2xl bg-emerald-50/90 border-2 border-emerald-500/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-3">
              {targetProfessionalAvatar ? (
                <img
                  src={targetProfessionalAvatar}
                  alt={targetProfessionalName || 'Selected Professional'}
                  className="w-11 h-11 rounded-xl object-cover border border-emerald-300 shrink-0"
                />
              ) : (
                <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white font-black flex items-center justify-center text-sm shrink-0">
                  {(targetProfessionalName || 'P').charAt(0)}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded-md bg-emerald-600 text-white text-[10px] font-extrabold uppercase tracking-wider">
                    Direct Artisan Assignment
                  </span>
                  <span className="text-xs font-extrabold text-emerald-950">
                    {targetProfessionalName || 'Selected Professional'}
                    {targetProfessionalBusiness ? ` (${targetProfessionalBusiness})` : ''}
                  </span>
                </div>
                <p className="text-[11px] text-emerald-800 mt-0.5">
                  This job will be sent directly to <strong>{targetProfessionalName || 'this professional'}</strong> and will not be displayed to unrelated trades.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setTargetProfessionalId(undefined);
                setTargetProfessionalName(undefined);
                setTargetProfessionalBusiness(undefined);
                setTargetProfessionalAvatar(undefined);
              }}
              className="px-3 py-1.5 rounded-xl bg-white hover:bg-rose-50 text-slate-600 hover:text-rose-700 border border-slate-200 text-[11px] font-bold transition-colors shrink-0 cursor-pointer self-start sm:self-center"
            >
              Open to All Qualified {selectedCategoryObj?.name || 'Artisans'}s Instead
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8 text-xs">
          
          {/* STEP 1: NATURAL PROBLEM INPUT (No category required) */}
          <div className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl bg-slate-50/70 border border-slate-200 space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-xs sm:text-sm font-bold text-slate-900">
                1. Describe Your Problem <span className="text-rose-500">*</span>
              </label>
              <span className="text-[11px] text-slate-400 font-medium hidden sm:inline">
                Plain English or Nigerian context welcomed
              </span>
            </div>

            {/* Quick Example Chips */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                Popular Common Problems (Tap to try):
              </span>
              <div className="flex flex-wrap gap-1.5">
                {NATURAL_PROBLEM_PROMPTS.map((ex, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectExample(ex.text)}
                    className="px-2.5 py-1 rounded-xl bg-white hover:bg-emerald-50 hover:border-emerald-300 border border-slate-200 text-slate-700 hover:text-emerald-800 text-[11px] font-medium transition-all shadow-2xs cursor-pointer text-left"
                  >
                    💡 {ex.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Main Natural Textarea */}
            <div>
              <textarea
                id="job-description-input"
                required
                rows={4}
                placeholder="e.g. My kitchen sink has been leaking from underneath for two days, or my generator starts but goes off after a few minutes, or my AC is blowing air but not cold..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-2xl text-xs sm:text-sm focus:outline-hidden focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white shadow-2xs leading-relaxed"
              />
            </div>

            {/* Job Title / Headline (Optional, auto-generated by AI if blank) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="font-semibold text-slate-700 text-xs">
                  Job Headline / Title <span className="text-slate-400 font-normal">(Optional — AI will create one if empty)</span>
                </label>
              </div>
              <input
                type="text"
                id="job-title-input"
                placeholder="e.g. Kitchen sink pipe leakage repair"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs sm:text-sm focus:outline-hidden focus:border-emerald-500 bg-white"
              />
            </div>

            {/* VISUAL ATTACHMENTS (Photos & Videos) */}
            <div className="pt-2 border-t border-slate-200/80 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-800 text-xs flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5 text-emerald-600" />
                  Attach Photos or Video of Fault (Optional)
                </span>
                <span className="text-[10px] text-slate-400">
                  Multimodal AI visual inspection
                </span>
              </div>

              {/* Photo preview list */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {photos.map((photoUrl, index) => (
                  <div key={index} className="relative group rounded-xl overflow-hidden aspect-video bg-slate-100 border border-slate-200">
                    <img src={photoUrl} alt="Fault preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-1.5 right-1.5 p-1 rounded-full bg-slate-900/80 text-white hover:bg-rose-600 transition-colors min-w-[24px] min-h-[24px] flex items-center justify-center cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                <label className="border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-xl p-2.5 flex flex-col items-center justify-center text-center cursor-pointer transition-colors aspect-video bg-white hover:bg-emerald-50/30">
                  <Upload className="w-4 h-4 text-emerald-600 mb-0.5" />
                  <span className="font-bold text-[11px] text-slate-700">Add Photo</span>
                  <span className="text-[9px] text-slate-400">JPG, PNG (max 5MB)</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </label>

                {/* Video Picker */}
                <label className="border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-xl p-2.5 flex flex-col items-center justify-center text-center cursor-pointer transition-colors aspect-video bg-white hover:bg-emerald-50/30">
                  <Video className="w-4 h-4 text-emerald-600 mb-0.5" />
                  <span className="font-bold text-[11px] text-slate-700">Add Video</span>
                  <span className="text-[9px] text-slate-400">MP4, WebM (max 50MB)</span>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleVideoFileSelect(e.target.files)}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Video preview badges */}
              {videoPreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {videoPreviews.map((vid, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-white border border-slate-200 px-2.5 py-1 rounded-lg text-[11px] text-slate-700">
                      <Film className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="font-medium truncate max-w-[120px]">{vid.name}</span>
                      <span className="text-[10px] text-slate-400">({vid.sizeMb}MB)</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveVideo(idx)}
                        className="text-slate-400 hover:text-rose-600 ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {videoError && (
                <p className="text-[11px] text-rose-600 font-medium">{videoError}</p>
              )}
            </div>

            {/* ACTION: TRIGGER AI UNDERSTANDING BUTTON */}
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-slate-700 font-medium flex items-center gap-1.5">
                <Info className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Gemini analyzes your description & photos, detects the required trade, and writes a complete diagnostic scope.</span>
              </div>

              <button
                type="button"
                id="run-ai-estimator-btn"
                onClick={() => runAiJobUnderstanding(undefined, undefined, true)}
                disabled={isEstimating || (!description.trim() && photos.length === 0)}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm shadow-sm transition-all flex items-center justify-center gap-2 shrink-0 min-h-[44px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEstimating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                    <span>Understanding Problem via AI...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    <span>Analyze Problem with JobMatch AI</span>
                  </>
                )}
              </button>
            </div>

            {aiError && (
              <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-300 text-rose-900 text-xs sm:text-sm font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{aiError}</span>
              </div>
            )}
          </div>

          {/* COMPLETE, HIGH-CONTRAST 4-SECTION AI PROBLEM SOLVER ANALYSIS */}
          {(aiAnalysisResult || aiEstimate || intentValidation) && (
            <div
              id="jobmatch-ai-analysis-panel"
              className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-slate-50 border-2 border-emerald-300 shadow-md space-y-5 text-slate-900"
            >
              {/* Panel Top Header */}
              <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-emerald-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-extrabold text-slate-900">
                      JobMatch AI Problem Analysis & Diagnostic Write-Up
                    </h3>
                    <p className="text-xs text-slate-700 font-medium">
                      Full technical assessment, possible causes, resolution steps, safety precautions, and cost benchmark
                    </p>
                  </div>
                </div>
              </div>

              {switchedSuccessNotice && (
                <div className="p-3 rounded-xl bg-emerald-100 border border-emerald-300 text-emerald-950 flex items-center gap-2 text-xs sm:text-sm font-bold">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span>{switchedSuccessNotice}</span>
                </div>
              )}

              {intentValidation && intentValidation.intent !== 'VALID_JOB' && (
                <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-300 text-amber-950 text-xs sm:text-sm space-y-3">
                  <p className="font-extrabold text-amber-950 leading-relaxed">{intentValidation.message}</p>
                  {Array.isArray(intentValidation.clarificationQuestions) &&
                    intentValidation.clarificationQuestions.length > 0 && (
                      <div className="space-y-3 pt-1">
                        <span className="font-extrabold text-xs uppercase tracking-wider text-amber-900 block">
                          Questions to Help Clarify Your Request:
                        </span>
                        {intentValidation.clarificationQuestions.map((q, idx) => (
                          <div key={idx} className="space-y-1.5">
                            <p className="text-xs sm:text-sm font-bold text-slate-900">
                              {idx + 1}. {q}
                            </p>
                            <input
                              type="text"
                              value={conversationAnswers[q] || ''}
                              onChange={(e) => handleAnswerQuestion(q, e.target.value)}
                              placeholder="Type your answer here..."
                              className="w-full px-3.5 py-2.5 rounded-xl border border-amber-400 !bg-white !text-slate-900 text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}

              {aiAnalysisResult && (!intentValidation || intentValidation.intent === 'VALID_JOB') && (() => {
                const primaryTrade = aiAnalysisResult.recommended_service || aiAnalysisResult.detected_category || 'Specialist';
                const secondaryTrades =
                  aiAnalysisResult.secondary_categories ||
                  aiClassification?.secondaryServices ||
                  [];
                const multiIssueNote =
                  aiAnalysisResult.multi_issue_details ||
                  aiClassification?.multiIssueDetails ||
                  '';

                const rawHypotheses =
                  aiAnalysisResult.root_cause_hypotheses ||
                  aiEstimate?.rootCauseHypotheses ||
                  [];
                const fallbackPossibleCauses = aiAnalysisResult.possible_causes || [];
                const rankedCauses =
                  rawHypotheses.length > 0
                    ? rawHypotheses
                    : fallbackPossibleCauses.map((causeText, idx) => ({
                        cause: causeText,
                        likelihood: (idx === 0 ? 'High' : idx === 1 ? 'Medium' : 'Low') as 'High' | 'Medium' | 'Low',
                        technicalExplanation: 'Requires physical on-site inspection by the matched professional to confirm.',
                      }));

                const resolutionSteps =
                  (aiAnalysisResult.suggested_resolution_for_professional &&
                  aiAnalysisResult.suggested_resolution_for_professional.length > 0
                    ? aiAnalysisResult.suggested_resolution_for_professional
                    : aiEstimate?.suggestedResolutionForProfessional &&
                      aiEstimate.suggestedResolutionForProfessional.length > 0
                    ? aiEstimate.suggestedResolutionForProfessional
                    : aiAnalysisResult.inspection_protocol || aiEstimate?.inspectionProtocol || []);

                const inspectionProtocolSteps =
                  aiAnalysisResult.inspection_protocol || aiEstimate?.inspectionProtocol || [];

                const toolsList =
                  aiAnalysisResult.tools_and_equipment ||
                  aiEstimate?.toolsAndEquipment ||
                  [];

                const materialsList = aiEstimate?.recommendedMaterials || [];

                const prepNotes =
                  aiAnalysisResult.professional_preparation_notes ||
                  aiEstimate?.professionalPreparationNotes ||
                  [];

                const unresolvedDetails =
                  aiAnalysisResult.unresolved_details_to_verify ||
                  aiEstimate?.unresolvedDetailsToVerify ||
                  [];

                const precautionsList =
                  aiAnalysisResult.customer_precautions && aiAnalysisResult.customer_precautions.length > 0
                    ? aiAnalysisResult.customer_precautions
                    : aiEstimate?.customerPrecautions && aiEstimate.customerPrecautions.length > 0
                    ? aiEstimate.customerPrecautions
                    : aiEstimate?.safetyTips
                    ? [aiEstimate.safetyTips]
                    : [];

                const preventiveAdvice =
                  aiAnalysisResult.preventive_maintenance_advice ||
                  aiEstimate?.preventiveMaintenanceAdvice ||
                  '';

                const customerQuestions =
                  aiAnalysisResult.customer_clarification_questions &&
                  aiAnalysisResult.customer_clarification_questions.length > 0
                    ? aiAnalysisResult.customer_clarification_questions
                    : aiEstimate?.customerClarificationQuestions &&
                      aiEstimate.customerClarificationQuestions.length > 0
                    ? aiEstimate.customerClarificationQuestions
                    : aiAnalysisResult.follow_up_questions && aiAnalysisResult.follow_up_questions.length > 0
                    ? aiAnalysisResult.follow_up_questions
                    : aiEstimate?.questionsToAskArtisan || [];

                const requiredSkills = aiAnalysisResult.required_skills || [];
                const itemizedBreakdown = aiEstimate?.itemizedCostBreakdown;

                return (
                  <div className="space-y-4">
                    {/* Header: Detected Trade(s), Ballpark Estimate & Full Problem Summary */}
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-3 py-1 rounded-lg bg-emerald-700 text-white font-extrabold text-xs sm:text-sm shadow-2xs">
                            Recommended Trade: {primaryTrade}
                          </span>
                          {secondaryTrades.map((secTrade, idx) => (
                            <span
                              key={idx}
                              className="px-2.5 py-1 rounded-lg bg-teal-100 text-teal-950 border border-teal-300 font-extrabold text-xs"
                            >
                              + {secTrade}
                            </span>
                          ))}
                          {aiAnalysisResult.severity_score && (
                            <span
                              className={`px-2.5 py-1 rounded-lg font-extrabold text-xs border ${
                                aiAnalysisResult.severity_score === 'Critical Hazard' ||
                                aiAnalysisResult.severity_score === 'High'
                                  ? 'bg-rose-100 text-rose-900 border-rose-300'
                                  : 'bg-amber-100 text-amber-900 border-amber-300'
                              }`}
                            >
                              Severity: {aiAnalysisResult.severity_score}
                            </span>
                          )}
                          {aiAnalysisResult.estimated_job_complexity && (
                            <span className="px-2.5 py-1 rounded-lg bg-slate-200 text-slate-900 font-bold text-xs capitalize">
                              Complexity: {aiAnalysisResult.estimated_job_complexity}
                            </span>
                          )}
                          {aiEstimate?.laborEstimateDays && (
                            <span className="px-2.5 py-1 rounded-lg bg-blue-100 text-blue-950 border border-blue-300 font-bold text-xs">
                              Est. Duration: {aiEstimate.laborEstimateDays}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                          {aiEstimate?.estimatedBudgetNaira?.recommended && (
                            <button
                              type="button"
                              onClick={() => setBudgetEstimate(aiEstimate.estimatedBudgetNaira.recommended)}
                              className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-extrabold text-xs cursor-pointer shadow-xs"
                            >
                              Use Recommended Budget: ₦{aiEstimate.estimatedBudgetNaira.recommended.toLocaleString()}
                            </button>
                          )}
                          {aiClassification?.categoryMismatch && (
                            <button
                              type="button"
                              id="switch-category-inline-btn"
                              onClick={() => handleSwitchCategoryDirectly(aiClassification.detectedService)}
                              className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs cursor-pointer"
                            >
                              Switch to {aiClassification.detectedService}
                            </button>
                          )}
                        </div>
                      </div>

                      {multiIssueNote && (
                        <div className="p-3.5 rounded-xl bg-teal-50 border border-teal-300 text-teal-950 text-xs sm:text-sm font-semibold leading-relaxed">
                          <strong className="font-extrabold">Multi-Trade Request Notice:</strong> {multiIssueNote}
                        </div>
                      )}

                      {/* Full Problem Summary Write-Up */}
                      <div className="p-4 rounded-xl bg-white border border-slate-300 shadow-2xs space-y-2">
                        <span className="text-xs font-extrabold uppercase tracking-wider text-emerald-800 block">
                          AI Problem Summary & Technical Assessment:
                        </span>
                        <p className="text-sm sm:text-base text-slate-900 font-medium leading-relaxed whitespace-pre-line">
                          {aiAnalysisResult.problem_summary}
                        </p>

                        {requiredSkills.length > 0 && (
                          <div className="pt-2 border-t border-slate-200 flex flex-wrap items-center gap-1.5">
                            <span className="text-xs font-extrabold text-slate-800 mr-1">Required Artisan Skills:</span>
                            {requiredSkills.map((skill, sIdx) => (
                              <span
                                key={sIdx}
                                className="px-2.5 py-0.5 rounded-md bg-emerald-50 text-emerald-950 border border-emerald-200 text-xs font-bold"
                              >
                                ✓ {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Price Benchmark & Itemized Cost Breakdown Write-Up */}
                      {aiEstimate?.estimatedBudgetNaira && (
                        <div className="p-4 rounded-xl bg-white border border-emerald-200 shadow-2xs space-y-2.5">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-xs font-extrabold uppercase tracking-wider text-emerald-900">
                              Estimated Nigerian Market Cost Range:
                            </span>
                            <span className="text-sm sm:text-base font-black text-emerald-800">
                              ₦{aiEstimate.estimatedBudgetNaira.min.toLocaleString()} – ₦
                              {aiEstimate.estimatedBudgetNaira.max.toLocaleString()} (Recommended: ₦
                              {aiEstimate.estimatedBudgetNaira.recommended.toLocaleString()})
                            </span>
                          </div>

                          {itemizedBreakdown && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 text-xs">
                              <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                                <span className="text-slate-600 font-bold block">Diagnostic Visit Fee:</span>
                                <span className="text-slate-900 font-extrabold text-sm">
                                  ₦{(itemizedBreakdown.diagnosticVisitFee || 0).toLocaleString()}
                                </span>
                              </div>
                              <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                                <span className="text-slate-600 font-bold block">Estimated Labor:</span>
                                <span className="text-slate-900 font-extrabold text-sm">
                                  ₦{(itemizedBreakdown.laborMin || 0).toLocaleString()} – ₦
                                  {(itemizedBreakdown.laborMax || 0).toLocaleString()}
                                </span>
                              </div>
                              <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                                <span className="text-slate-600 font-bold block">Estimated Materials:</span>
                                <span className="text-slate-900 font-extrabold text-sm">
                                  ₦{(itemizedBreakdown.materialsMin || 0).toLocaleString()} – ₦
                                  {(itemizedBreakdown.materialsMax || 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          )}

                          {aiEstimate.costRangeExplanation && (
                            <p className="text-xs sm:text-sm text-slate-800 font-medium leading-relaxed pt-1">
                              <strong className="text-slate-900">Cost Note:</strong> {aiEstimate.costRangeExplanation}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Grid of 4 Detailed, High-Contrast Sections */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                      {/* SECTION 1: POSSIBLE CAUSES */}
                      <div className="p-4 rounded-xl bg-white border border-slate-300 shadow-2xs space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="font-extrabold text-slate-900 uppercase tracking-wider text-xs sm:text-sm">
                            1. Possible Causes (Ranked)
                          </h4>
                          <span className="px-2.5 py-0.5 rounded-md bg-amber-100 text-amber-950 border border-amber-300 text-xs font-bold">
                            Possibilities • Unconfirmed
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed">
                          Ranked most likely to least likely. Physical on-site inspection is required to confirm the exact fault:
                        </p>
                        <ol className="space-y-2.5">
                          {rankedCauses.map((hyp, idx) => (
                            <li
                              key={idx}
                              className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1.5"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <span className="font-extrabold text-slate-900 text-xs sm:text-sm leading-snug">
                                  {idx + 1}. {hyp.cause}
                                </span>
                                <span
                                  className={`shrink-0 px-2.5 py-0.5 rounded-md text-xs font-extrabold ${
                                    hyp.likelihood === 'High'
                                      ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                                      : hyp.likelihood === 'Medium'
                                      ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                      : 'bg-slate-200 text-slate-800 border border-slate-300'
                                  }`}
                                >
                                  {hyp.likelihood === 'High'
                                    ? 'Most Likely'
                                    : hyp.likelihood === 'Medium'
                                    ? 'Possible'
                                    : 'Less Likely'}
                                </span>
                              </div>
                              <p className="text-xs sm:text-sm text-slate-800 font-normal leading-relaxed">
                                {hyp.technicalExplanation}
                              </p>
                            </li>
                          ))}
                        </ol>
                      </div>

                      {/* SECTION 2: SUGGESTED RESOLUTION FOR THE PROFESSIONAL */}
                      <div className="p-4 rounded-xl bg-white border border-slate-300 shadow-2xs space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="font-extrabold text-slate-900 uppercase tracking-wider text-xs sm:text-sm">
                            2. Suggested Resolution for the Professional
                          </h4>
                          <span className="px-2.5 py-0.5 rounded-md bg-emerald-100 text-emerald-950 border border-emerald-300 text-xs font-bold">
                            {primaryTrade}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed">
                          Recommended inspection sequence and repair approach for the matched artisan:
                        </p>
                        <ul className="space-y-2 text-xs sm:text-sm text-slate-900">
                          {resolutionSteps.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-2 leading-relaxed">
                              <span className="text-emerald-700 font-extrabold">{idx + 1}.</span>
                              <span className="font-medium text-slate-800">{step}</span>
                            </li>
                          ))}
                        </ul>

                        {inspectionProtocolSteps.length > 0 &&
                          JSON.stringify(inspectionProtocolSteps) !== JSON.stringify(resolutionSteps) && (
                            <div className="pt-2.5 border-t border-slate-200 space-y-1.5">
                              <span className="text-xs font-extrabold text-slate-900 block">
                                On-Site Diagnostic Protocol:
                              </span>
                              <ul className="space-y-1.5 text-xs sm:text-sm text-slate-800">
                                {inspectionProtocolSteps.map((step, idx) => (
                                  <li key={idx} className="flex items-start gap-2 leading-relaxed">
                                    <span className="text-emerald-700 font-bold">•</span>
                                    <span>{step}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                        {(toolsList.length > 0 || materialsList.length > 0 || prepNotes.length > 0 || unresolvedDetails.length > 0) && (
                          <div className="pt-2.5 border-t border-slate-200 space-y-2 text-xs sm:text-sm">
                            {toolsList.length > 0 && (
                              <p className="text-slate-800 leading-relaxed">
                                <strong className="text-slate-900 font-extrabold">Tools & Equipment to Bring:</strong>{' '}
                                {toolsList.join(', ')}
                              </p>
                            )}
                            {materialsList.length > 0 && (
                              <p className="text-slate-800 leading-relaxed">
                                <strong className="text-slate-900 font-extrabold">Potential Materials / Parts (If Verified On Site):</strong>{' '}
                                {materialsList.join('; ')}
                              </p>
                            )}
                            {prepNotes.length > 0 && (
                              <div className="space-y-1">
                                <strong className="text-slate-900 font-extrabold block">Professional Preparation Notes:</strong>
                                <ul className="space-y-1 text-slate-800">
                                  {prepNotes.map((note, nIdx) => (
                                    <li key={nIdx} className="flex items-start gap-1.5 leading-relaxed">
                                      <span className="text-emerald-700 font-bold">•</span>
                                      <span>{note}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {unresolvedDetails.length > 0 && (
                              <div className="space-y-1">
                                <strong className="text-slate-900 font-extrabold block">Details to Verify On Site:</strong>
                                <ul className="space-y-1 text-slate-800">
                                  {unresolvedDetails.map((item, uIdx) => (
                                    <li key={uIdx} className="flex items-start gap-1.5 leading-relaxed">
                                      <span className="text-amber-700 font-bold">•</span>
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* SECTION 3: CUSTOMER PRECAUTIONS */}
                      <div className="p-4 rounded-xl bg-white border border-slate-300 shadow-2xs space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="font-extrabold text-slate-900 uppercase tracking-wider text-xs sm:text-sm">
                            3. Customer Precautions & Safety
                          </h4>
                          <span
                            className={`px-2.5 py-0.5 rounded-md text-xs font-bold ${
                              safetyHazard || aiAnalysisResult.safety_warning
                                ? 'bg-rose-100 text-rose-950 border border-rose-300'
                                : 'bg-blue-100 text-blue-950 border border-blue-300'
                            }`}
                          >
                            {safetyHazard || aiAnalysisResult.safety_warning ? 'Urgent Safety Priority' : 'Before Artisan Arrives'}
                          </span>
                        </div>

                        {(safetyHazard || aiAnalysisResult.safety_warning) && (
                          <div className="p-3.5 rounded-xl bg-rose-50 border-2 border-rose-300 text-rose-950 text-xs sm:text-sm space-y-1.5">
                            <span className="font-extrabold text-rose-950 flex items-center gap-1.5">
                              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                              <span>
                                {typeof safetyHazard === 'object' && safetyHazard?.warningTitle
                                  ? safetyHazard.warningTitle
                                  : 'Urgent Safety & Evacuation Warning'}
                              </span>
                            </span>
                            <p className="text-rose-900 leading-relaxed font-semibold">
                              {typeof safetyHazard === 'string'
                                ? safetyHazard
                                : safetyHazard?.immediateInstructions ||
                                  safetyHazard?.actionRequired ||
                                  (typeof aiAnalysisResult.safety_warning === 'string'
                                    ? aiAnalysisResult.safety_warning
                                    : '')}
                            </p>
                          </div>
                        )}

                        <ul className="space-y-2 text-xs sm:text-sm text-slate-900">
                          {precautionsList.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 leading-relaxed">
                              <span className="text-amber-700 font-extrabold">•</span>
                              <span className="font-medium text-slate-800">{item}</span>
                            </li>
                          ))}
                        </ul>

                        {preventiveAdvice && (
                          <div className="pt-2.5 border-t border-slate-200 space-y-1">
                            <span className="text-xs font-extrabold text-emerald-900 block">
                              Preventive Maintenance Advice:
                            </span>
                            <p className="text-xs sm:text-sm text-slate-800 font-medium leading-relaxed">
                              {preventiveAdvice}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* SECTION 4: QUESTIONS FOR THE CUSTOMER */}
                      <div className="p-4 rounded-xl bg-white border border-slate-300 shadow-2xs space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="font-extrabold text-slate-900 uppercase tracking-wider text-xs sm:text-sm">
                            4. Questions for the Customer
                          </h4>
                          <span className="px-2.5 py-0.5 rounded-md bg-slate-200 text-slate-900 text-xs font-bold">
                            Optional Clarifications
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed">
                          Answer any applicable questions below to help the professional prepare before arriving:
                        </p>
                        <div className="space-y-3">
                          {customerQuestions.map((q, idx) => (
                            <div key={idx} className="space-y-1.5">
                              <p className="text-xs sm:text-sm font-bold text-slate-900 leading-snug">
                                {idx + 1}. {q}
                              </p>
                              <input
                                type="text"
                                value={conversationAnswers[q] || ''}
                                onChange={(e) => handleAnswerQuestion(q, e.target.value)}
                                placeholder="Your short answer (optional)..."
                                className="w-full px-3.5 py-2 rounded-xl border border-slate-300 !bg-white !text-slate-900 text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* STEP 2: SERVICE CATEGORY & LOCATION IN NIGERIA */}
          <div className="space-y-4 bg-slate-50/80 p-4 sm:p-5 rounded-2xl border border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-200/80">
              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-900">
                  2. Trade & Service Location <span className="text-rose-500">*</span>
                </label>
                <p className="text-[11px] text-slate-500">
                  Selected trade: <strong className="text-emerald-700">{selectedCategoryObj?.name || categorySlug}</strong>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowCategoryPicker((prev) => !prev)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:border-slate-300 text-slate-700 text-xs font-semibold cursor-pointer w-fit"
              >
                <span>{showCategoryPicker ? 'Hide Trades' : 'Change Trade'}</span>
                {showCategoryPicker ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>

            {showCategoryPicker && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {SERVICE_CATEGORIES.map((cat) => {
                  const isSelected = categorySlug === cat.slug;
                  return (
                    <button
                      key={cat.slug}
                      type="button"
                      id={`select-cat-${cat.slug}-btn`}
                      onClick={() => {
                        setCategorySlug(cat.slug);
                        setUserManuallySelectedCategory(true);
                        setShowCategoryPicker(false);
                      }}
                      className={`p-2.5 rounded-xl border text-left flex items-center gap-2 transition-all cursor-pointer ${
                        isSelected
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-900 font-bold'
                          : 'border-slate-200 bg-white text-slate-700'
                      }`}
                    >
                      <CategoryIcon slug={cat.slug} className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="text-[11px] font-bold leading-tight">{cat.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <LocationMapInput
              initialState={state}
              initialLga={lga}
              initialCityArea={cityArea}
              initialAddress={address}
              initialLatitude={locationLatitude}
              initialLongitude={locationLongitude}
              onLocationSelected={(locData: SelectedLocationData) => {
                setState(locData.state);
                setLga(locData.lga);
                if (locData.cityArea) setCityArea(locData.cityArea);
                setAddress(locData.address);
                if (locData.formattedAddress) setFormattedAddress(locData.formattedAddress);
                setLocationLatitude(locData.latitude);
                setLocationLongitude(locData.longitude);
                setPlaceId(locData.placeId);
                setLocationConfirmed(locData.locationConfirmed);
                setLocationConfidence(
                  locData.detectionMethod === 'manual_dropdown' || locData.detectionMethod === 'manual_selection'
                    ? 'manual_confirmed'
                    : 'geoapify_confirmed'
                );
                setLgaConfirmed(locData.lgaConfirmed);
              }}
              required={true}
            />

            <div>
              <input
                type="text"
                placeholder="Nearest landmark or gate description (optional, e.g. Near First Bank)"
                value={locationLandmark}
                onChange={(e) => setLocationLandmark(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs sm:text-sm focus:outline-hidden focus:border-emerald-500 min-h-[42px] bg-white placeholder:text-slate-400"
              />
            </div>
          </div>

          {/* STEP 3: URGENCY & TARGET BUDGET */}
          <div className="space-y-4 bg-slate-50/80 p-4 sm:p-5 rounded-2xl border border-slate-200">
            <label className="block text-xs sm:text-sm font-bold text-slate-900">
              3. Urgency & Target Budget
            </label>

            <div className="grid grid-cols-3 gap-2">
              {[
                { level: 'emergency' as UrgencyLevel, label: '🚨 Today / Urgent' },
                { level: 'within_48h' as UrgencyLevel, label: '⚡ Within 48h' },
                { level: 'flexible' as UrgencyLevel, label: '🗓️ Flexible' },
              ].map((item) => (
                <button
                  key={item.level}
                  type="button"
                  onClick={() => setUrgency(item.level)}
                  className={`py-2.5 px-2 sm:px-3 rounded-xl border text-center font-bold text-[11px] sm:text-xs transition-all cursor-pointer ${
                    urgency === item.level
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-500/20'
                      : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-1 mb-1">
                <label className="block font-semibold text-slate-700 text-xs">
                  Target Budget in Naira (Optional)
                </label>
                {aiEstimate?.estimatedBudgetNaira?.recommended && (
                  <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                    AI Benchmark: ₦{aiEstimate.estimatedBudgetNaira.recommended.toLocaleString()}
                  </span>
                )}
              </div>
              <input
                type="number"
                placeholder="e.g. 25000 (Leave blank to receive open quotes)"
                value={budgetEstimate}
                onChange={(e) => setBudgetEstimate(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs sm:text-sm focus:outline-hidden focus:border-emerald-500 min-h-[44px] bg-white font-medium"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[15000, 25000, 50000, 80000, 150000].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setBudgetEstimate(preset)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors cursor-pointer ${
                      budgetEstimate === preset
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    ₦{preset.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* SUBMIT BUTTON */}
          <div className="pt-1">
            <button
              type="submit"
              id="submit-job-btn"
              onClick={() => {
                explicitSubmitAllowedRef.current = true;
              }}
              disabled={isSubmitting || isAnalyzingAI}
              className="w-full py-3.5 sm:py-4 px-4 sm:px-6 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs sm:text-base shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-h-[52px] text-center leading-snug"
            >
              {isSubmitting || isAnalyzingAI ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin shrink-0" />
                  <span>Matching Local Professionals...</span>
                </>
              ) : (
                <>
                  <Wrench className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <span>Post Job — Pay Only When You Accept a Professional</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                </>
              )}
            </button>
            <p className="text-center text-[11px] text-slate-500 mt-2">
              Free to post • When a professional accepts, you receive a Telegram & in-app link to complete Paystack escrow payment
            </p>
          </div>
        </form>
      </div>

      {/* CATEGORY MISMATCH CONFIRMATION MODAL */}
      {showCategoryMismatchModal && pendingAnalysis && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl space-y-4 animate-in zoom-in-95">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-600">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">
                    AI Category Recommendation
                  </h3>
                  <span className="text-[11px] text-slate-500">
                    Review trade before dispatching to local professionals
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCategoryMismatchModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Comparison Box */}
            <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 space-y-1.5">
              <span className="font-bold text-xs text-amber-900 block">
                Trade Recommendation Notice
              </span>
              <p className="text-xs text-amber-900 leading-relaxed">
                You selected <strong>{selectedCategoryObj?.name || categorySlug}</strong>, but based on your description, this fault is best diagnosed and repaired by a certified <strong className="text-emerald-800 underline">{pendingAnalysis.detected_category}</strong>.
              </p>
              {pendingClassification?.mismatchExplanation && (
                <p className="text-[11px] text-amber-800 bg-amber-100/60 p-2 rounded-lg font-medium">
                  {pendingClassification.mismatchExplanation}
                </p>
              )}
            </div>

            {/* Physical Reality Safeguard note inside modal */}
            <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-[10px] text-slate-500">
              AI suggestion based on your description. The professional will confirm the exact cause and price during physical inspection.
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 pt-1">
              <button
                type="button"
                id="accept-ai-category-btn"
                onClick={handleAcceptDetectedCategory}
                className="w-full py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Switch to {pendingAnalysis.detected_category} (Recommended)</span>
              </button>

              <button
                type="button"
                id="keep-customer-category-btn"
                onClick={handleKeepOriginalCategory}
                className="w-full py-2.5 px-4 rounded-xl bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs border border-slate-200 transition-all cursor-pointer"
              >
                <span>Keep My Choice ({selectedCategoryObj?.name || categorySlug})</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
